import {
  EEventContextAction,
  EEventType,
  type ICountryCurrencyPrice,
  type ICurrencyPrice,
  type ITranslation,
} from '@mahawi/eshop-common/dist/src/types';
import { type Dispatch } from '@reduxjs/toolkit';
import React, { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { connect } from 'react-redux';

import { getHead } from '../../head';
import LinearProgress from '../components/linear-progress';
import Photos from '../components/photos';
import Breadcrumbs from '../components/product/breadcrumbs';
import Description from '../components/product/description';
import LanguageMutationLinks from '../components/product/language-mutation-links';
import Offer from '../components/product/offer';
import Properties from '../components/product/properties';
import Related from '../components/product/related';
import { type IConfigState } from '../reducers/config/types';
import { eventSend } from '../reducers/event/reducer';
import { type ILanguageState } from '../reducers/language/types';
import { type IProductState } from '../reducers/product/types';
import { setActiveCategory } from '../reducers/products/reducer';
import { type IUserState } from '../reducers/user/types';
import { type ITagManager } from '../types';

function ProductContainer({
  Product,
  Config,
  dispatch,
  Language,
  TagManager,
  User,
}: {
  Product: IProductState;
  Config: IConfigState;
  dispatch: Dispatch;
  Language: ILanguageState;
  TagManager: ITagManager | undefined;
  User: IUserState;
}): JSX.Element {
  const { t } = useTranslation();

  useEffect((): (() => void) => {
    dispatch(
      eventSend({
        type: EEventType.PAGE_PRODUCT,
        action: EEventContextAction.ENTER,
        context: { product: { uuid: Product.product?.uuid } },
      }),
    );

    return (): void => {
      dispatch(
        eventSend({
          type: EEventType.PAGE_PRODUCT,
          action: EEventContextAction.LEAVE,
          context: { product: { uuid: Product.product?.uuid } },
        }),
      );
    };
  }, [dispatch, Product.product?.uuid]);

  useEffect((): void => {
    if (!Product.product) {
      return;
    }

    const [category] = Product.product.categories;

    if (!category) {
      return;
    }

    dispatch(
      setActiveCategory({
        category: {
          uuid: category.uuid,
        },
      }),
    );
  }, [Product.product, Product.product?.categories, dispatch]);

  useEffect((): void => {
    if (!TagManager) {
      return;
    }

    const productNameEn: ITranslation | undefined = Product.product?.names.find(
      (n: ITranslation): boolean => n.code === 'EN',
    );
    const productPriceEur: ICurrencyPrice | undefined =
      Product.product?.prices.find(
        (c: ICurrencyPrice): boolean => c.isoCode === 'EUR',
      );
    const priceWithTax: ICountryCurrencyPrice | undefined =
      productPriceEur?.pricesWithTax.find(
        (c: ICountryCurrencyPrice): boolean =>
          c.isoCode === (User.countryType?.isoCode || 'CZE'),
      );

    TagManager.dataLayer({
      dataLayer: {
        event: 'view_item',
        ecommerce: {
          items: [
            {
              item_id: Product.product?.uuid,
              item_name: productNameEn?.value || '---',
              price: priceWithTax || 0,
              currency: 'EUR',
            },
          ],
        },
      },
    });
  }, [
    Product.product?.names,
    Product.product?.prices,
    TagManager,
    Product.product?.uuid,
  ]);

  if (Product.isLoading) {
    return <LinearProgress />;
  }

  const {
    languageType: { code: languageCode },
  } = Language;

  let name: ITranslation | undefined = Product.product?.names.find(
    (n: ITranslation): boolean => n.code === languageCode,
  );

  if (!name) {
    name = Product.product?.names.find(
      (n: ITranslation): boolean => n.code === 'EN',
    );
  }

  if (typeof document !== 'undefined' && name) {
    const { title } = getHead(t, name ? name.value : '');
    document.title = title;
  }

  const headerTwoElements: string[] = [];

  if (Product.product?.brand && Product.product?.brand.name) {
    headerTwoElements.push(Product.product?.brand.name);
  }

  if (Product.product?.mpn) {
    headerTwoElements.push(Product.product?.mpn);
  }

  if (Product.product?.gtin) {
    headerTwoElements.push(Product.product?.gtin);
  }

  const headerTwo: string = headerTwoElements.join(' | ');

  if (!Product.product) {
    return <div>...</div>;
  }

  return (
    <>
      <div className="product container">
        <Breadcrumbs />
        <h1>{name ? name.value : ''}</h1>
        <h2>{headerTwo}</h2>
      </div>

      <Photos
        photos={Product.product.photos}
        cdn={Config.cdn}
        name={name ? name.value : ''}
        dispatch={dispatch}
      />

      <Offer TagManager={TagManager} />

      <Description />

      <Properties />

      <Related />

      <LanguageMutationLinks />
    </>
  );
}

const mapStateToProps = ({ Config, Language, User, Product }) => ({
  Config,
  Language,
  User,
  Product,
});

export default connect(mapStateToProps)(ProductContainer);
